import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import useTranslation from "../../hooks/useTranslation";
import {
  QuestionBreakdownDemographic,
  QuestionBreakdownDemographicGroup,
} from "../../types/workplaceCulture";
import { getQuestionResponseChartData } from "../../utils/QuestionBreakdown.utils";
import { QuestionBreakdownNoData } from "../QuestionBreakdownNoData/QuestionBreakdownNoData";
import { Typography } from "../ui/Typography/Typography";
import "./QuestionBreakdownChart.scss";

interface QuestionBreakdownChartProps {
  breakdownDemographicGroup: QuestionBreakdownDemographicGroup;
  useCombinedOptions?: boolean;
  showIsSkip?: boolean;
  isMultiSelect?: boolean;
}

export const QuestionBreakdownChart = ({
  breakdownDemographicGroup,
  useCombinedOptions = true,
  showIsSkip = false,
  isMultiSelect = false,
}: QuestionBreakdownChartProps) => {
  const { t } = useTranslation("workplaceCulturePage");

  const breakdownDemographicGroupDidNotReachThreshold =
    breakdownDemographicGroup.demographics?.every(
      (demographic: QuestionBreakdownDemographic) =>
        demographic.options.every((option) => option.count === null)
    );

  const breakdownDemographicGroupHasNoAnswer =
    breakdownDemographicGroup.demographics?.every(
      (demographic: QuestionBreakdownDemographic) =>
        demographic.options.every((option) => option.count === 0)
    );

  if (
    breakdownDemographicGroupDidNotReachThreshold ||
    breakdownDemographicGroupHasNoAnswer
  ) {
    return (
      <QuestionBreakdownNoData
        breakdownDemographicGroup={breakdownDemographicGroup}
        customMessage={
          breakdownDemographicGroupHasNoAnswer
            ? t("surveyDemographics.noOneCheckedAnswer")
            : undefined
        }
      />
    );
  }

  const { chartData: demographicsChartData, skipSeriesData } =
    getQuestionResponseChartData(
      t,
      breakdownDemographicGroup,
      useCombinedOptions,
      showIsSkip,
      isMultiSelect
    );

  return (
    <div className="QuestionBreakdownChart">
      <HighchartsReact
        highcharts={Highcharts}
        options={demographicsChartData}
        containerProps={{
          "data-testid": "breakdown-chart-container",
          className: "QuestionBreakdownChart__chart",
        }}
      />
      {skipSeriesData.length > 0 && (
        <div className="QuestionBreakdownChart__skipSeries">
          {skipSeriesData.map((demographic) => (
            <div key={demographic.label}>
              <Typography desktop="caption" weight="bold">
                {demographic.label}
              </Typography>
              {demographic.data.map((data) => (
                <Typography
                  desktop="caption"
                  fontStyle="italic"
                  key={data.text}
                >
                  {data.text}: {data.y}%
                </Typography>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
